import { useState } from 'react'
import { Bell } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  useMarkAllRead,
  useMarkRead,
  useNotificationList,
  useUnreadCount,
} from '@/hooks/useNotifications'
import { formatRelativeTime } from '@/lib/relativeTime'
import { formatBadgeCount, typeStyle } from '@/lib/notificationStyles'
import { notificationLink } from '@/lib/notificationLink'
import { cn } from '@/lib/utils'
import type { Notification } from '@/api/notificationsApi'

// How many recent notifications the popover shows; the full list lives at
// /notifications.
const POPOVER_LIMIT = 7

const ROW_CLASS =
  'flex w-full items-start gap-2.5 border-b px-4 py-3 text-left last:border-b-0 hover:bg-muted/50'

/**
 * One row in the popover. Actionable notifications (those that resolve to a
 * route) render as a real <Link> — so middle-click / cmd-click / "open in new
 * tab" all behave — and opening one marks it read. Non-actionable ones stay a
 * plain button that only marks read.
 */
function NotificationRow({
  notification: n,
  onMarkRead,
  onNavigate,
}: {
  notification: Notification
  onMarkRead: (id: string) => void
  onNavigate: () => void
}) {
  const link = notificationLink(n)
  const isUnread = n.readAt === null
  const s = typeStyle(n.type)

  const body = (
    <>
      <span
        className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', isUnread ? s.dot : 'bg-transparent')}
      />
      <span className='flex-1'>
        <span className='block text-sm text-foreground'>{n.message}</span>
        <span className='mt-0.5 block text-xs text-muted-foreground'>
          {formatRelativeTime(n.createdAt)}
        </span>
      </span>
    </>
  )

  if (!link) {
    return (
      <button
        onClick={() => isUnread && onMarkRead(n.id)}
        className={cn(ROW_CLASS, !isUnread && 'opacity-60')}
      >
        {body}
      </button>
    )
  }

  return (
    <Link
      to={link}
      onClick={() => {
        if (isUnread) onMarkRead(n.id)
        onNavigate()
      }}
      className={cn(ROW_CLASS, !isUnread && 'opacity-60')}
    >
      {body}
    </Link>
  )
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const { data: unread } = useUnreadCount()
  const count = unread?.count ?? 0

  const { data, isLoading } = useNotificationList({ unreadOnly: false })
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()
  const items = (data?.items ?? []).slice(0, POPOVER_LIMIT)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
          className='relative flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <Bell className='h-4 w-4' />
          {count > 0 && (
            <span className='absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white'>
              {formatBadgeCount(count)}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align='end' className='w-80 p-0'>
        <div className='flex items-center justify-between border-b px-4 py-2.5'>
          <span className='text-sm font-semibold text-foreground'>Notifications</span>
          {count > 0 && (
            <button
              onClick={() => markAllRead.mutate(undefined)}
              disabled={markAllRead.isPending}
              className='text-xs font-medium text-primary hover:underline disabled:opacity-50'
            >
              Mark all read
            </button>
          )}
        </div>

        <div className='max-h-80 overflow-y-auto'>
          {isLoading ? (
            <p className='px-4 py-6 text-center text-sm text-muted-foreground'>Loading…</p>
          ) : items.length === 0 ? (
            <p className='px-4 py-6 text-center text-sm text-muted-foreground'>
              You’re all caught up
            </p>
          ) : (
            items.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onMarkRead={(id) => markRead.mutate(id)}
                onNavigate={() => setOpen(false)}
              />
            ))
          )}
        </div>

        <div className='border-t px-4 py-2'>
          <Link
            to='/notifications'
            onClick={() => setOpen(false)}
            className='block text-center text-xs font-medium text-primary hover:underline'
          >
            View all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  )
}
